import { Core_ContactMsg } from '@/generated/graphql';
import {
  ExportOutlined,
  MailOutlined,
  MobileOutlined,
  PhoneOutlined,
  PrinterOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import { Card, Col, Divider, Row, Space, Typography } from 'antd';
import Title from 'antd/lib/typography/Title';
import { Maybe } from 'graphql/jsutils/Maybe';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { buildAddress, buildPhoneNumber } from '../../helpers/formatters';
import {
  buildAddressAdditionString,
  buildAddressStringShort,
  extractPrimaryAddress,
} from '../../helpers/utils';
import { Tenant, TENANT } from '../../tenant/tenant';
import { AveliosButton } from '../common-layout/avelios-button';

const { Link } = Typography;

interface FacilityContactProps {
  facilityName: Maybe<string> | undefined;
  facilityContactNote: Maybe<string> | undefined;
  contact: Maybe<Core_ContactMsg> | undefined;
}

export const FacilityContact: FC<FacilityContactProps> = ({
  contact,
  facilityName,
  facilityContactNote,
}) => {
  const { formatMessage } = useIntl();

  const primaryAddress = extractPrimaryAddress(contact);

  const address = buildAddressStringShort(primaryAddress);
  const addressAddition = buildAddressAdditionString(primaryAddress);

  const fullAddress = buildAddress(primaryAddress);

  const email =
    (contact?.emails?.find((email) => email?.primary) ?? contact?.emails?.[0])?.email || '-';

  const landlineNumbers = contact?.landlineNumbers;
  const mobileNumbers = contact?.mobileNumbers;
  const faxNumbers = contact?.faxNumbers;

  return (
    <Card>
      <Title level={3}>
        {formatMessage(
          { id: 'facility-selection.contact.title' },
          { facilityName: facilityName || '' },
        )}
      </Title>

      <Space style={{ width: '100%' }} direction={'vertical'} size={8}>
        <Row>
          <Col span={2}>
            <PushpinOutlined />
          </Col>
          <Col flex={'fill'}>
            <div>{address}</div>
            <div>{addressAddition}</div>
          </Col>
        </Row>
        <Row>
          <Col span={2}>
            <MailOutlined />
          </Col>
          <Col flex={'fill'}>{email}</Col>
        </Row>
        {landlineNumbers?.map((number) => {
          const numberString = buildPhoneNumber(number);
          return (
            <Row key={number?.id}>
              <Col span={2}>
                <PhoneOutlined />
              </Col>
              <Col flex={'fill'}>{numberString}</Col>
            </Row>
          );
        })}
        {mobileNumbers?.map((number) => {
          const numberString = buildPhoneNumber(number);
          return (
            <Row key={number?.id}>
              <Col span={2}>
                <MobileOutlined />
              </Col>
              <Col flex={'fill'}>{numberString}</Col>
            </Row>
          );
        })}
        {faxNumbers?.map((number) => {
          const numberString = buildPhoneNumber(number);
          return (
            <Row key={number?.id}>
              <Col span={2}>
                <PrinterOutlined />
              </Col>
              <Col flex={'fill'}>{numberString}</Col>
            </Row>
          );
        })}
      </Space>
      <AveliosButton
        block
        style={{ marginTop: 24 }}
        onClick={() => {
          window.open(`https://maps.google.com/?q=${fullAddress}`, '_blank');
        }}
      >
        {formatMessage({ id: 'action.open-in-maps' })}
      </AveliosButton>
      <Divider />
      <p>
        {!!facilityContactNote
          ? facilityContactNote
          : formatMessage({ id: 'facility-selection.contact.emergency' })}
      </p>
      {TENANT === Tenant.sana && (
        <Link href="https://www.doctolib.de/" target="_blank">
          {formatMessage({ id: 'facility-selection.contact.doctolib' })} <ExportOutlined />
        </Link>
      )}
    </Card>
  );
};
